'use client';

import * as React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { cn } from '../../lib/cn';

export interface SliderProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>,
    'value' | 'defaultValue' | 'onValueChange'
  > {
  value?: number;
  defaultValue?: number;
  onValueChange?: (value: number) => void;
  /** Show the current value to the right of the track. */
  showValue?: boolean;
  /** Suffix appended to the shown value (e.g. "px"). */
  suffix?: string;
}

/** Range slider — powers the design customizer knobs (e.g. Roundness). */
export const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(function Slider(
  { className, value, defaultValue = 50, onValueChange, showValue = false, suffix = '', min = 0, max = 100, step = 1, disabled, ...rest },
  ref,
) {
  const [internal, setInternal] = React.useState(defaultValue);
  const current = value ?? internal;

  const handle = (vals: number[]) => {
    const next = vals[0] ?? 0;
    if (value === undefined) setInternal(next);
    onValueChange?.(next);
  };

  return (
    <div className="flex w-full items-center gap-[var(--space-3)]">
      <SliderPrimitive.Root
        ref={ref}
        value={[current]}
        onValueChange={handle}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        className={cn(
          'relative flex h-5 w-full touch-none select-none items-center',
          disabled && 'opacity-50',
          className,
        )}
        {...rest}
      >
        <SliderPrimitive.Track className="relative h-1.5 w-full grow rounded-full bg-muted">
          <SliderPrimitive.Range className="absolute h-full rounded-full bg-accent" />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          aria-label="value"
          className={cn(
            'block size-4 rounded-full border-2 border-accent bg-white shadow-sm outline-none',
            'transition-transform focus-visible:scale-110',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
        />
      </SliderPrimitive.Root>
      {showValue ? (
        <span className="min-w-10 text-right font-mono text-sm text-fg-secondary tabular-nums">
          {current}
          {suffix}
        </span>
      ) : null}
    </div>
  );
});
